/**
 * CateringTeamPricingMatrix — pro-side editor for the catering-team
 * 2-axis price table: number of guests × number of menu items.
 *
 * The header banner reminds the pro that prices include cooking, serving,
 * cleanup, and transport-within-city — these are NOT additional add-ons.
 *
 * Backend: /api/me/catering-team/matrix (GET / PUT)
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CookingPot, ForkKnife, Broom, Truck, Plus, Trash, FloppyDisk,
  Spinner, Info, X,
} from '@phosphor-icons/react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const INCLUDED_CHIPS = [
  { icon: CookingPot, label: 'Cooking' },
  { icon: ForkKnife,  label: 'Serving' },
  { icon: Broom,      label: 'Cleanup' },
  { icon: Truck,      label: 'Transport (within city)' },
];

const fetchMatrix = async () => {
  const { data } = await axios.get(
    `${BACKEND_URL}/api/me/catering-team/matrix`,
    { withCredentials: true },
  );
  return data;
};

const saveMatrix = async (payload) => {
  const { data } = await axios.put(
    `${BACKEND_URL}/api/me/catering-team/matrix`,
    payload,
    { withCredentials: true },
  );
  return data;
};

const fmtINR = (n) => {
  const v = Number(n);
  if (!v) return '';
  return `₹${v.toLocaleString('en-IN')}`;
};

const CateringTeamPricingMatrix = () => {
  const [guestBuckets, setGuestBuckets] = useState([]);
  const [itemBuckets, setItemBuckets] = useState([]);
  const [prices, setPrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newGuests, setNewGuests] = useState('');
  const [newItems, setNewItems] = useState('');

  useEffect(() => {
    let mounted = true;
    fetchMatrix()
      .then((d) => {
        if (!mounted) return;
        setGuestBuckets(d.guest_buckets || []);
        setItemBuckets(d.item_buckets || []);
        setPrices(d.prices || {});
        setLoading(false);
      })
      .catch((err) => {
        if (!mounted) return;
        toast.error(err?.response?.data?.detail || 'Could not load pricing matrix');
        setLoading(false);
      });
    return () => { mounted = false; };
  }, []);

  const onCellChange = (g, i, value) => {
    setPrices((prev) => {
      const next = { ...prev };
      next[g] = { ...(next[g] || {}) };
      next[g][i] = value.replace(/[^\d]/g, '');
      return next;
    });
  };

  const addGuestBucket = () => {
    const v = parseInt(newGuests, 10);
    if (!v || v <= 0) { toast.error('Enter a positive guest count'); return; }
    if (guestBuckets.includes(v)) { toast.info('That guest tier already exists'); return; }
    const next = [...guestBuckets, v].sort((a, b) => a - b);
    setGuestBuckets(next);
    // Seed empty cells for the new row.
    setPrices((prev) => {
      const row = {};
      itemBuckets.forEach((i) => { row[String(i)] = 0; });
      return { ...prev, [String(v)]: row };
    });
    setNewGuests('');
  };

  const addItemBucket = () => {
    const v = parseInt(newItems, 10);
    if (!v || v <= 0) { toast.error('Enter a positive item count'); return; }
    if (itemBuckets.includes(v)) { toast.info('That item-count column already exists'); return; }
    const next = [...itemBuckets, v].sort((a, b) => a - b);
    setItemBuckets(next);
    // Seed empty cells for the new column.
    setPrices((prev) => {
      const out = { ...prev };
      guestBuckets.forEach((g) => {
        out[String(g)] = { ...(out[String(g)] || {}), [String(v)]: 0 };
      });
      return out;
    });
    setNewItems('');
  };

  const removeGuestBucket = (g) => {
    setGuestBuckets((prev) => prev.filter((x) => x !== g));
    setPrices((prev) => {
      const next = { ...prev };
      delete next[String(g)];
      return next;
    });
  };

  const removeItemBucket = (i) => {
    setItemBuckets((prev) => prev.filter((x) => x !== i));
    setPrices((prev) => {
      const next = {};
      Object.keys(prev).forEach((g) => {
        const row = { ...(prev[g] || {}) };
        delete row[String(i)];
        next[g] = row;
      });
      return next;
    });
  };

  const onSave = () => {
    setSaving(true);
    saveMatrix({
      guest_buckets: guestBuckets,
      item_buckets: itemBuckets,
      prices,
    })
      .then(() => toast.success('Pricing matrix saved'))
      .catch((err) => toast.error(err?.response?.data?.detail || 'Save failed'))
      .finally(() => setSaving(false));
  };

  if (loading) {
    return <div className="text-center py-6 text-sm text-text-secondary">Loading matrix…</div>;
  }

  return (
    <Card className="bg-white border-[#E8E1D5]" data-testid="catering-matrix-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <CookingPot size={22} weight="duotone" className="text-amber-600" />
          Pricing Matrix — Guests &times; Menu Items
        </CardTitle>
        <CardDescription>
          Customers will quote you off this table. Enter the <strong>all-in</strong>
          total price for each guest-count × menu-item combination.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Disclosure banner — make it impossible to miss */}
        <div className="mb-4 p-3 rounded border-2 border-emerald-300 bg-emerald-50"
          data-testid="catering-matrix-includes-banner">
          <div className="flex items-start gap-2 mb-2">
            <Info size={16} weight="fill" className="text-emerald-700 mt-0.5 flex-shrink-0" />
            <span className="text-sm font-bold text-emerald-900">
              Each price MUST include the following — do not add these as extras later:
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {INCLUDED_CHIPS.map(({ icon: Icon, label }) => (
              <span key={label}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-emerald-300 text-xs font-semibold text-emerald-800"
                data-testid={`catering-matrix-incl-${label.toLowerCase().split(' ')[0]}`}>
                <Icon size={14} weight="fill" />
                {label}
              </span>
            ))}
          </div>
        </div>

        {/* Add row / add column controls */}
        <div className="flex gap-3 flex-wrap mb-3">
          <div className="flex gap-1 items-center" data-testid="catering-matrix-add-row">
            <input
              type="number"
              min="1"
              placeholder="Guests (e.g. 750)"
              value={newGuests}
              onChange={(e) => setNewGuests(e.target.value)}
              className="px-2 py-1.5 text-xs border-2 border-[#E8E1D5] rounded w-32"
              data-testid="catering-matrix-add-row-input"
            />
            <Button size="sm" variant="outline" onClick={addGuestBucket}
              data-testid="catering-matrix-add-row-btn">
              <Plus size={12} className="mr-1" /> Row
            </Button>
          </div>
          <div className="flex gap-1 items-center" data-testid="catering-matrix-add-col">
            <input
              type="number"
              min="1"
              placeholder="Menu items (e.g. 12)"
              value={newItems}
              onChange={(e) => setNewItems(e.target.value)}
              className="px-2 py-1.5 text-xs border-2 border-[#E8E1D5] rounded w-32"
              data-testid="catering-matrix-add-col-input"
            />
            <Button size="sm" variant="outline" onClick={addItemBucket}
              data-testid="catering-matrix-add-col-btn">
              <Plus size={12} className="mr-1" /> Column
            </Button>
          </div>
        </div>

        {/* The matrix */}
        <div className="overflow-x-auto border border-[#E8E1D5] rounded">
          <table className="w-full text-sm" data-testid="catering-matrix-table">
            <thead>
              <tr className="bg-[#FAF6EE]">
                <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wide text-text-secondary border-b border-[#E8E1D5]">
                  Guests ↓ / Items →
                </th>
                {itemBuckets.map((i) => (
                  <th key={i} className="px-2 py-2 text-xs font-bold border-b border-[#E8E1D5] min-w-[110px]">
                    <div className="flex items-center justify-between gap-1">
                      <span>{i} items</span>
                      <button
                        type="button"
                        onClick={() => removeItemBucket(i)}
                        className="text-text-secondary hover:text-red-600"
                        title="Remove column"
                        data-testid={`catering-matrix-remove-col-${i}`}
                      >
                        <X size={11} />
                      </button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {guestBuckets.map((g) => (
                <tr key={g} className="border-b border-[#E8E1D5] last:border-b-0"
                  data-testid={`catering-matrix-row-${g}`}>
                  <th className="px-3 py-2 text-left font-bold bg-[#FAF6EE]/50">
                    <div className="flex items-center justify-between gap-2">
                      <span>{g} guests</span>
                      <button
                        type="button"
                        onClick={() => removeGuestBucket(g)}
                        className="text-text-secondary hover:text-red-600"
                        title="Remove row"
                        data-testid={`catering-matrix-remove-row-${g}`}
                      >
                        <Trash size={12} />
                      </button>
                    </div>
                  </th>
                  {itemBuckets.map((i) => {
                    const v = ((prices[String(g)] || {})[String(i)]) || '';
                    return (
                      <td key={i} className="px-2 py-1.5">
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-text-secondary pointer-events-none">₹</span>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={v}
                            onChange={(e) => onCellChange(String(g), String(i), e.target.value)}
                            placeholder="0"
                            className="w-full pl-5 pr-1 py-1.5 text-xs border border-[#E8E1D5] rounded focus:border-amber-500 focus:outline-none font-mono"
                            data-testid={`catering-matrix-cell-${g}-${i}`}
                          />
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Preview line — quick sanity for the pro */}
        {guestBuckets.length > 0 && itemBuckets.length > 0 && (
          <p className="text-[11px] text-text-secondary mt-2">
            Example: for <strong>{guestBuckets[0]} guests</strong> with <strong>{itemBuckets[0]} menu items</strong>,
            customer sees <strong>
              {fmtINR((prices[String(guestBuckets[0])] || {})[String(itemBuckets[0])]) || 'not set'}
            </strong> (all-in).
          </p>
        )}

        <div className="mt-4 flex justify-end">
          <Button onClick={onSave} disabled={saving}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="catering-matrix-save-btn">
            {saving
              ? <Spinner size={14} className="animate-spin mr-2" />
              : <FloppyDisk size={14} weight="fill" className="mr-2" />}
            Save Matrix
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default CateringTeamPricingMatrix;
